import React, { useEffect, useState } from "react";
import Footer from "../components/Footer";
import AOS from "aos";
import "aos/dist/aos.css";
import { Mail, Phone, MapPin, Facebook, Instagram } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import axios from "axios";

function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback("");

    try {
      await axios.post(`${BASE_URL}/api/contact`, formData);
      setFeedback("✅ Message sent successfully!");
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      setFeedback("❌ Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4">
        <Breadcrumbs />
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-blue-50 text-center">
        <div className="max-w-xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-blue-700 mb-4">Contact Us</h1>
          <p className="text-gray-600 text-lg">
            Need help, want to ask a question, or just want to say hi? We’d love to hear from you!
          </p>
        </div>
      </section>

      {/* Main Grid */}
      <section
        className="max-w-6xl mx-auto py-16 px-4 grid md:grid-cols-3 gap-10 items-stretch"
        data-aos="fade-up"
      >
        {/* Social Card */}
        <div className="md:col-span-1 h-full">
          <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col justify-between space-y-10">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="text-blue-600 w-6 h-6" />
                <div>
                  <h3 className="font-semibold text-blue-700">Email</h3>
                  <p className="text-gray-600">info@exudeehampers.com</p>
                  <p className="text-gray-600">support@exudeehampers.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-blue-600 w-6 h-6" />
                <div>
                  <h3 className="font-semibold text-blue-700">Phone</h3>
                  <p className="text-gray-600">+1 (469) 590-5754</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-blue-600 w-6 h-6" />
                <div>
                  <h3 className="font-semibold text-blue-700">Location</h3>
                  <p className="text-gray-600">9450 Skillman St suite 113, Dallas, TX 75243</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">Follow Us</h3>
              <div className="flex gap-4 text-blue-600">
                <a
                  href="https://web.facebook.com/profile.php?id=61579974249431"
                  target="_blank"
                  rel="noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-blue-600 hover:bg-blue-50 transition"
                >
                  <Facebook className="w-6 h-6 stroke-[1.5]" />
                </a>
                <a
                  href="https://www.instagram.com/exudeehampers/"
                  target="_blank"
                  rel="noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-blue-600 hover:bg-blue-50 transition"
                >
                  <Instagram className="w-6 h-6 stroke-[1.5]" />
                </a>
                <a
                  href="https://x.com/exudeehampers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-blue-600 hover:bg-blue-50 transition"
                >
                  <img src="/images/icons/x-icon.png" alt="X.com" className="w-6 h-6" />
                </a>
                <a
                  href="https://wa.me/14695905754"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-blue-600 hover:bg-blue-50 transition"
                >
                  <img src="/images/icons/whatsapp-icon.png" alt="WhatsApp" className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow p-6 h-full">
            <h2 className="text-2xl font-semibold text-blue-700 mb-4">Send a Message</h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <textarea
                name="message"
                placeholder="Your Message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              ></textarea>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
            {feedback && <p className="mt-4 text-center text-sm">{feedback}</p>}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="max-w-6xl mx-auto px-4 pb-16" data-aos="fade-up">
        <div className="w-full h-[400px] overflow-hidden rounded-lg shadow">
          <iframe
            title="Our Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3349.7606307850288!2d-96.71177109999999!3d32.90449649999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c1fd4008a0e1f%3A0xa30413391387dc40!2s9450%20Skillman%20St%20suite%20113%2C%20Dallas%2C%20TX%2075243%2C%20USA!5e0!3m2!1sen!2sng!4v1776763422337!5m2!1sen!2sng"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            className="w-full h-full"
          ></iframe>
        </div>
      </section>

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </>
  );
}

export default Contact;
